import * as THREE from "three";
import {
  MODEL_FRAMING,
  MODEL_ZOOM,
  SCENE_DEFAULTS,
} from "@/shared/config/constants";
import { createCameraRig } from "./createCamera";
import { createWebGLRenderer } from "./createRenderer";
import { createNeutralEnvironment, ENVIRONMENT } from "./createEnvironment";
import { createOrbitControls, type OrbitControls } from "./orbitControls";
import { applyResize } from "./resize";
import { loadGLBModel } from "./loadModel";
import { createContactShadow, type ContactShadow } from "./createContactShadow";
import type { SceneSurface } from "./types";
import { applyTexture as applyTextureToModel } from "./applyTexture";
import { applyMaterial as applyMaterialToModel } from "./applyMaterial";
import { switchMaterials as switchMaterialsInModel } from "./switchMaterials";
import { showPart as showPartInModel } from "./showPart";
import { setPartVisibility as setPartVisibilityInModel } from "./setPartVisibility";
import { applyVariant as applyVariantToModel } from "./applyVariant";
import {
  canTryAndroidSceneViewer,
  isIOSDevice,
  openAndroidSceneViewer,
  openQuickLookFromUSDZBlob,
} from "./arHelpers";
import { exportObjectToUSDZ, type ARExportOptions } from "./arExport";

export type ARLaunchResult =
  | "presenting"
  | "launched"
  | "unsupported"
  | "failed";

/**
 * Absolute scale applied in AR so the model appears at its true real-world size
 * instead of being re-fit to a fixed height (the old behaviour forced 1.1 m).
 * The webp GLB (chair_situs.webp.glb) is authored in metres — 1 source unit =
 * 1 m — so identity is correct for it. If you AR-export a model authored in
 * other units, recalibrate this value (source unit → metre).
 */
const AR_REAL_WORLD_SCALE = SCENE_DEFAULTS.arRealWorldScale;

type Scene3DOptions = {
  modelPath?: string;
  /** `null` keeps the canvas transparent (host page background shows through). */
  backgroundColor?: string | null;
  contactShadow?: boolean;
  textureRepeat?: number;
  textureTileSize?: number;
};

export class Scene3D {
  private mount: HTMLElement;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private camera: THREE.PerspectiveCamera;
  private model: THREE.Group | null = null;
  private controls: OrbitControls;
  private controlsAttached = false;
  private ro: ResizeObserver | null = null;
  private materialMap: Map<THREE.Mesh, THREE.MeshStandardMaterial> = new Map();
  private isRendering = false;
  private isDisposed = false;
  private resizeHandler: () => void;
  private modelPath: string;
  private contactShadowEnabled: boolean;
  private textureRepeat: number;
  private textureTileSize: number;
  private modelLoadPromise: Promise<void>;
  private defaultBackground: THREE.Scene["background"] = null;
  private arSession: XRSession | null = null;
  private arModelTransform: {
    position: THREE.Vector3;
    scale: THREE.Vector3;
  } | null = null;
  private onARSessionEnd: (() => void) | null = null;
  private textureCache: Map<string, THREE.Texture> = new Map(); // Cache for loaded textures
  private currentTextures: Set<THREE.Texture> = new Set(); // Track currently applied textures
  private textureLoader: THREE.TextureLoader = new THREE.TextureLoader(); // Reuse loader instance
  private clonedMaterials: Set<THREE.Material> = new Set();
  private contactShadow: ContactShadow | null = null; // Baked soft shadow under the model
  private shadowRebakeHandle: number | null = null; // Pending re-bake rAF id
  private environmentMap: THREE.Texture | null = null; // PMREM neutral env map used for IBL
  // Bounding sphere of the loaded model, in world units. Kept so the camera can
  // be re-framed whenever the viewer's aspect ratio changes.
  private modelBounds: THREE.Sphere | null = null;
  // The orbit target, shared by reference with the controls: mutating it moves
  // what the camera looks at, and the next `setRadius` re-applies the position.
  private cameraTarget: THREE.Vector3;
  // Stepped zoom offset from the default framing, in `MODEL_ZOOM` steps:
  // positive moves the camera closer. Re-applied by `frameModel`, so it survives
  // resizes and model swaps.
  private zoomStep = 0;

  constructor(mount: HTMLElement, options: Scene3DOptions = {}) {
    this.mount = mount;
    this.modelPath = options.modelPath ?? "/models/chair_situs.glb";
    this.contactShadowEnabled =
      options.contactShadow ?? SCENE_DEFAULTS.contactShadow;
    this.textureRepeat = options.textureRepeat ?? SCENE_DEFAULTS.textureRepeat;
    this.textureTileSize =
      options.textureTileSize ?? SCENE_DEFAULTS.textureTileSize;

    this.scene = new THREE.Scene();
    this.scene.environmentIntensity = ENVIRONMENT.MAP_INTENSITY;
    // `undefined` falls back to the default colour; an explicit `null` means the
    // scene stays transparent and the renderer clears with alpha 0.
    this.scene.background =
      options.backgroundColor === null
        ? null
        : new THREE.Color(
            options.backgroundColor ?? SCENE_DEFAULTS.backgroundColor,
          );
    this.defaultBackground = this.scene.background;

    const rig = createCameraRig();
    this.camera = rig.camera;
    this.cameraTarget = rig.target;
    this.scene.add(this.camera);

    this.renderer = createWebGLRenderer();
    this.mount.appendChild(this.renderer.domElement);

    this.createEnvironment();

    this.controls = createOrbitControls({
      camera: this.camera,
      target: rig.target,
    });
    this.attachControls();

    this.resize();
    this.ro = new ResizeObserver(() => {
      this.resize();
      this.updateBorderRadius();
    });
    this.ro.observe(this.mount);

    // Update border radius initially and on window resize
    this.updateBorderRadius();
    this.resizeHandler = () => this.updateBorderRadius();
    window.addEventListener("resize", this.resizeHandler);

    // Start rendering immediately (empty scene)
    this.start();

    // Load model asynchronously
    this.modelLoadPromise = this.loadModel();
    void this.modelLoadPromise.catch(() => undefined);
  }

  private updateBorderRadius() {
    // A transparent canvas paints nothing but the model, so there is no surface
    // to round — clipping it would only cut the model near the corners.
    if (this.scene.background === null) {
      this.renderer.domElement.style.borderRadius = "0";
      return;
    }

    // Mobile: only top corners rounded, Desktop: only left corners
    const isMobile = window.innerWidth < 768;
    this.renderer.domElement.style.borderRadius = isMobile
      ? "0.75rem 0.75rem 0 0"
      : "0 0 0 0";
  }

  getModelPath(): string {
    return this.modelPath;
  }

  getModelUrl(): string {
    return this.resolveAssetUrl(this.modelPath);
  }

  setARSessionEndHandler(handler: (() => void) | null): void {
    this.onARSessionEnd = handler;
  }

  async waitUntilModelReady(): Promise<void> {
    await this.modelLoadPromise.catch(() => undefined);
  }

  private async loadModel() {
    // No reachability pre-check: it doubled a request on every model load, and
    // the loader below already reports the URL it could not fetch. Requests out
    // of this project go through data/api, which a check here would bypass.
    const modelUrl = this.resolveAssetUrl(this.modelPath);

    try {
      const model = await loadGLBModel(modelUrl);
      this.model = model;

      // Store materials for color changes
      const allObjectNames: string[] = [];
      const podruckaObjects: Array<{
        name: string;
        type: string;
        hasMeshes: boolean;
        meshCount: number;
      }> = [];

      model.traverse((child) => {
        allObjectNames.push(child.name);
        if (child instanceof THREE.Mesh) {
          if (child.material instanceof THREE.MeshStandardMaterial) {
            // Tune how strongly the IBL environment lights/reflects on this material.
            child.material.envMapIntensity = ENVIRONMENT.MAP_INTENSITY;
            this.materialMap.set(
              child as THREE.Mesh<
                THREE.BufferGeometry,
                THREE.MeshStandardMaterial
              >,
              child.material,
            );
          }
        }

        // Check podrucka objects and their mesh content
        if (child.name.toLowerCase().includes("podrucka")) {
          let meshCountInObject = 0;
          child.traverse((descendant) => {
            if (descendant instanceof THREE.Mesh) {
              meshCountInObject++;
            }
          });
          podruckaObjects.push({
            name: child.name,
            type: child.type,
            hasMeshes: meshCountInObject > 0,
            meshCount: meshCountInObject,
          });
        }
      });

      // Center and scale model
      const box = new THREE.Box3().setFromObject(model);
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      // Increase scale from 1.5 to 1.8 to make model bigger
      const scale = maxDim > 0 ? 1.8 / maxDim : 1;

      // Reset position and scale first
      model.position.set(0, 0, 0);
      model.scale.set(scale, scale, scale);

      // Recalculate bounding box after scaling
      box.setFromObject(model);
      const center = box.getCenter(new THREE.Vector3());

      // Center the model (center it at origin)
      model.position.sub(center);

      // Adjust Y position to center vertically (raise it up more)
      // The model is now centered at origin, so we can adjust Y to position it higher
      model.position.y += 0.5; // Raise the model up more

      this.scene.add(model);

      this.modelBounds = new THREE.Box3()
        .setFromObject(model)
        .getBoundingSphere(new THREE.Sphere());
      this.frameModel();

      if (this.contactShadowEnabled) {
        this.addGroundShadow(model);
      }

      // Emit event when model is loaded so SceneRenderer can apply initial params
      this.mount.dispatchEvent(new CustomEvent("modelLoaded"));
    } catch (error) {
      console.error("Failed to load model:", error);
      console.error(
        "Error details:",
        error instanceof Error ? error.stack : String(error),
      );
      // Don't add fallback - let user see the error
      throw error;
    }
  }

  start() {
    if (this.isRendering) return;
    this.isRendering = true;
    this.renderer.setAnimationLoop(() => {
      this.renderer.render(this.scene, this.camera);
    });
  }

  resize() {
    applyResize({
      camera: this.camera,
      renderer: this.renderer,
      cssWidth: this.mount.clientWidth,
      cssHeight: this.mount.clientHeight,
    });
    // The frame's aspect ratio decides which axis is the tight one, so the
    // framing has to be redone whenever the viewer's proportions change.
    this.frameModel();
  }

  /**
   * Points the camera at the model's centre and pulls it back just far enough for
   * the model to fill the frame, on whichever axis is tighter for the current
   * aspect ratio.
   *
   * Without this the camera sat at a hard-coded distance aimed at a fixed height,
   * so the model floated low in the frame with dead space above it, and a change
   * of viewer proportions (a taller box, a narrower embed) made it worse rather
   * than being compensated for. The orbit direction and the user's rotation are
   * preserved — only the distance and the target move.
   *
   * The fit is against the bounding sphere, which is invariant under rotation:
   * fitting the bounding box instead is tighter but only holds for one camera
   * angle — the tilt makes the projected height exceed the box height, and the
   * model's legs get clipped at the bottom edge.
   */
  private frameModel() {
    const bounds = this.modelBounds;
    if (!bounds || bounds.radius <= 0) return;
    if (this.arSession) return; // AR uses real-world placement, not screen fit.

    const fovY = THREE.MathUtils.degToRad(this.camera.fov);
    const aspect = this.camera.aspect > 0 ? this.camera.aspect : 1;
    const fovX = 2 * Math.atan(Math.tan(fovY / 2) * aspect);
    // Whichever axis is tighter for the current proportions decides the distance.
    const fov = Math.min(fovY, fovX);

    const distance =
      (bounds.radius / Math.sin(fov / 2) / Math.max(MODEL_FRAMING.FILL, 0.01)) *
      Math.pow(MODEL_ZOOM.STEP, this.zoomStep);

    // Aiming below the centre pushes the model up in the frame without touching
    // the model's own transform.
    this.cameraTarget.copy(bounds.center);
    this.cameraTarget.y -= bounds.radius * MODEL_FRAMING.LIFT;
    this.controls.setRadius(distance);
  }

  /**
   * Moves the camera to a zoom step, clamped to the range `MODEL_ZOOM` allows.
   * Returns the step that was actually applied.
   */
  setZoomStep(step: number): number {
    this.zoomStep = THREE.MathUtils.clamp(
      Math.round(step),
      -MODEL_ZOOM.OUT_STEPS,
      MODEL_ZOOM.IN_STEPS,
    );
    this.frameModel();
    return this.zoomStep;
  }

  /**
   * Loads a texture from URL with caching
   */
  private loadTexture(url: string): Promise<THREE.Texture> {
    const cached = this.textureCache.get(url);
    if (cached) return Promise.resolve(cached);

    return new Promise((resolve, reject) => {
      this.textureLoader.load(
        url,
        (texture) => {
          // Cache the texture
          this.textureCache.set(url, texture);
          resolve(texture);
        },
        undefined,
        (error) => {
          reject(error instanceof Error ? error : new Error(String(error)));
        },
      );
    });
  }

  /**
   * Configures fabric texture map settings for tiled repetition
   */
  private configureFabricMap(map: THREE.Texture): void {
    map.flipY = false;
    map.wrapS = THREE.RepeatWrapping;
    map.wrapT = THREE.RepeatWrapping;

    // Optimize texture filtering for better performance
    map.minFilter = THREE.LinearMipmapLinearFilter;
    map.magFilter = THREE.LinearFilter;

    // Limit texture size to reduce memory usage (max 2K for textures)
    const maxSize = SCENE_DEFAULTS.textureMaxSize;
    if (map.image) {
      const img = map.image as HTMLImageElement;
      if (img.width > maxSize || img.height > maxSize) {
        // Texture will be automatically resized by Three.js if needed
        // but we can optimize by using lower quality settings
        map.generateMipmaps = true;
      }
    }

    // Use anisotropic filtering only if supported and reasonable
    map.anisotropy = Math.min(
      this.renderer.capabilities.getMaxAnisotropy(),
      SCENE_DEFAULTS.textureAnisotropyLimit,
    );

    map.needsUpdate = true;
  }

  /**
   * Bakes a soft contact shadow under the model (render-to-texture + blur) and
   * adds it to the scene — a wide, very soft shadow that darkens under the feet
   * and dissolves outward, matching the make3db reference. Baked once here since
   * it doesn't change as the orbit camera moves.
   */
  private addGroundShadow(model: THREE.Group): void {
    const modelBox = new THREE.Box3().setFromObject(model);
    const floorY = modelBox.min.y;
    const modelSize = modelBox.getSize(new THREE.Vector3());
    const center = modelBox.getCenter(new THREE.Vector3());

    this.contactShadow = createContactShadow({
      renderer: this.renderer,
      scene: this.scene,
      floorY,
      center,
      size: modelSize,
    });
    this.scene.add(this.contactShadow.group);
    this.contactShadow.bake();
  }

  /**
   * Queues a re-bake of the contact shadow. The shadow is a texture captured
   * from the scene's current geometry, so it goes stale as soon as parts are
   * shown or hidden — without this, a removed part keeps casting its shadow and
   * an added one casts none. Coalesced into a single rAF so a burst of part
   * toggles (a config applying many options at once) bakes only once.
   */
  private scheduleShadowRebake(): void {
    if (!this.contactShadow || this.isDisposed) return;
    if (this.shadowRebakeHandle !== null) return;

    this.shadowRebakeHandle = requestAnimationFrame(() => {
      this.shadowRebakeHandle = null;
      if (this.isDisposed || !this.contactShadow) return;
      this.contactShadow.bake();
    });
  }

  /**
   * Builds the neutral studio IBL environment map and assigns it as the scene's
   * image-based lighting (used for lighting/reflections only — the solid
   * background colour is preserved). Synchronous: RoomEnvironment needs no asset
   * download. Strength is controlled by `scene.environmentIntensity` (set in the
   * constructor) and each material's `envMapIntensity` (set on model load).
   */
  private createEnvironment(): void {
    this.environmentMap = createNeutralEnvironment(this.renderer);
    this.scene.environment = this.environmentMap;
  }

  /**
   * Finds nodes by name (simple case-insensitive partial match)
   */
  private findNodesByName(name: string): THREE.Object3D[] {
    if (!this.model || !name) return [];

    const nodes: THREE.Object3D[] = [];
    const searchName = this.normalize(name);

    this.model.traverse((child) => {
      if (!child || typeof child.name !== "string" || !child.name) return;

      const childName = this.normalize(child.name);
      if (childName.includes(searchName)) {
        nodes.push(child);
      }
    });

    return nodes;
  }

  /**
   * Applies a swatch option's texture to its `applyTo` model nodes.
   */
  async applyTexture(surface: SceneSurface | undefined): Promise<void> {
    if (!this.model || !surface?.texture) return;

    await applyTextureToModel({
      model: this.model,
      texture: surface.texture,
      applyTo: surface.applyTo ?? [],
      normalMap: surface.normalMap,
      bumpMap: surface.bumpMap,
      reflexMap: surface.reflexMap,
      tileSize: this.textureTileSize,
      repeat: this.textureRepeat,
      loadTexture: (url) => this.loadTexture(this.resolveAssetUrl(url)),
      configureFabricMap: (map) => {
        this.configureFabricMap(map);
        this.currentTextures.add(map); // Track applied texture
      },
      findNodesByName: (name) => this.findNodesByName(name),
      onTextureApplied: (texture) => {
        this.currentTextures.add(texture);
      },
    });
  }

  /**
   * Gets canvas screenshot as data URL with dimensions
   */
  getScreenshot(): { dataUrl: string; width: number; height: number } | null {
    if (!this.renderer || !this.isRendering) {
      return null;
    }
    if (!this.renderer.domElement) {
      return null;
    }
    // Force render to ensure latest state
    this.renderer.render(this.scene, this.camera);
    try {
      const canvas = this.renderer.domElement;
      const dataUrl = canvas.toDataURL("image/png", 1.0);
      return {
        dataUrl,
        width: canvas.width,
        height: canvas.height,
      };
    } catch (error) {
      console.error("Failed to get screenshot:", error);
      return null;
    }
  }

  async isARSupported(): Promise<boolean> {
    try {
      return !!(await navigator.xr?.isSessionSupported?.("immersive-ar"));
    } catch {
      return false;
    }
  }

  isARPresenting(): boolean {
    return this.renderer.xr.isPresenting || this.arSession !== null;
  }

  async enterAR(): Promise<boolean> {
    if (this.isARPresenting()) return true;
    if (!navigator.xr?.requestSession) return false;

    try {
      const session = await navigator.xr.requestSession("immersive-ar", {
        requiredFeatures: ["local-floor"],
        optionalFeatures: ["dom-overlay"],
        domOverlay: { root: this.mount },
      });

      this.arSession = session;
      session.addEventListener("end", this.handleARSessionEnd, { once: true });

      this.detachControls();
      this.prepareModelForWebXR();
      this.scene.background = null;
      this.renderer.xr.enabled = true;
      this.renderer.xr.setReferenceSpaceType("local-floor");
      await this.renderer.xr.setSession(session);

      return true;
    } catch {
      this.handleARSessionEnd();
      return false;
    }
  }

  async exitAR(): Promise<void> {
    const session = this.renderer.xr.getSession() ?? this.arSession;
    if (!session) return;

    try {
      await session.end();
    } catch {
      this.handleARSessionEnd();
    }
  }

  async openAR(): Promise<ARLaunchResult> {
    try {
      if (isIOSDevice()) {
        const blob = await this.exportModelUSDZ({
          // Real-world size in AR instead of forcing a fixed 1.1 m height.
          absoluteScale: AR_REAL_WORLD_SCALE,
        });
        if (!blob) return "failed";

        const href = openQuickLookFromUSDZBlob(blob, { arPlacement: "floor" });
        if (!href) return "failed";
        if (href.startsWith("blob:")) {
          window.setTimeout(() => URL.revokeObjectURL(href), 30000);
        }

        return "launched";
      }

      if (await this.isARSupported()) {
        return (await this.enterAR()) ? "presenting" : "failed";
      }

      if (canTryAndroidSceneViewer()) {
        return openAndroidSceneViewer(this.getModelUrl())
          ? "launched"
          : "failed";
      }

      return "unsupported";
    } catch (error) {
      console.error("Failed to open AR:", error);
      return "failed";
    }
  }

  async exportModelUSDZ(options: ARExportOptions = {}): Promise<Blob | null> {
    await this.waitUntilModelReady();
    if (!this.model) return null;

    return exportObjectToUSDZ(this.model, options);
  }

  private handleARSessionEnd = () => {
    this.arSession = null;
    this.scene.background = this.defaultBackground;
    this.restoreModelAfterWebXR();

    if (!this.isDisposed && this.mount.contains(this.renderer.domElement)) {
      this.attachControls();
    }

    this.onARSessionEnd?.();
  };

  private prepareModelForWebXR(): void {
    if (!this.model || this.arModelTransform) return;

    this.arModelTransform = {
      position: this.model.position.clone(),
      scale: this.model.scale.clone(),
    };

    // Show the model at its true real-world size (the GLB is authored in
    // metres), overriding the on-screen display fit — no height-forcing in AR.
    this.model.scale.setScalar(AR_REAL_WORLD_SCALE);

    const scaledBox = new THREE.Box3().setFromObject(this.model);
    const center = scaledBox.getCenter(new THREE.Vector3());

    this.model.position.x -= center.x;
    this.model.position.y -= scaledBox.min.y;
    this.model.position.z += -1.5 - center.z;
  }

  private restoreModelAfterWebXR(): void {
    if (!this.model || !this.arModelTransform) {
      this.arModelTransform = null;
      return;
    }

    this.model.position.copy(this.arModelTransform.position);
    this.model.scale.copy(this.arModelTransform.scale);
    this.arModelTransform = null;
  }

  private attachControls(): void {
    if (this.controlsAttached) return;
    this.controls.attach(this.renderer.domElement);
    this.controlsAttached = true;
  }

  private detachControls(): void {
    if (!this.controlsAttached) return;
    this.controls.detach();
    this.controlsAttached = false;
  }

  /**
   * Normalizes a string for comparison (lowercase, trim)
   */
  private normalize(name: string): string {
    return (name || "").toLowerCase().trim();
  }

  /**
   * Resolves asset URL against widget base URL.
   * This keeps /models and /textures loading from the widget host domain
   * even when the widget is embedded into a different site.
   */
  private resolveAssetUrl(url: string): string {
    if (!url) return url;
    if (
      /^(https?:)?\/\//i.test(url) ||
      url.startsWith("data:") ||
      url.startsWith("blob:")
    ) {
      return url;
    }
    return url;
  }

  /**
   * Finds and shows a part of the model by name
   * @param partName - Category name to search for (e.g., "armrest", "headrest")
   * @param modelName - Specific model variant to show, or 'None' to hide all
   * @returns true if any part was found, false otherwise
   */
  showPart(partName: string, modelName: string): boolean {
    if (!this.model) return false;

    const changed = showPartInModel({
      model: this.model,
      scene: this.scene,
      partName,
      modelName,
      findNodesByName: (name) => this.findNodesByName(name),
      normalize: (name) => this.normalize(name),
    });

    if (changed) this.scheduleShadowRebake();

    return changed;
  }

  /**
   * Toggles a single named part on or off without affecting sibling parts.
   * Used by multi-select `gridOptions` groups (e.g. accessories).
   * @param modelName - Node name to search for (e.g. "Taburet", "Pillow")
   * @param visible - Whether the matched part should be shown
   * @returns true if any matching node was found, false otherwise
   */
  setPartVisibility(modelName: string, visible: boolean): boolean {
    if (!this.model) return false;

    const changed = setPartVisibilityInModel({
      modelName,
      visible,
      findNodesByName: (name) => this.findNodesByName(name),
      normalize: (name) => this.normalize(name),
    });

    if (changed) this.scheduleShadowRebake();

    return changed;
  }

  /**
   * Switches the model to a named `KHR_materials_variants` variant, respecting
   * the model's authored per-node materials. Used by configs whose GLB ships
   * proper glTF material variants (e.g. the main Omnia chair).
   */
  applyVariant(variantName: string): boolean {
    if (!this.model) return false;

    return applyVariantToModel({
      model: this.model,
      variantName,
      variantNames:
        (this.model.userData.materialVariants as string[] | undefined) ?? [],
      materialsByIndex:
        (this.model.userData.variantMaterials as
          | THREE.Material[]
          | undefined) ?? [],
    });
  }

  /**
   * Swaps a built-in GLB material (by its authored name) onto the option's
   * target nodes. Used by configs whose models ship material variants instead
   * of external textures.
   */
  switchMaterials(surface: SceneSurface | undefined): void {
    if (!this.model || !surface?.material) return;

    switchMaterialsInModel({
      model: this.model,
      materialName: surface.material,
      applyTo: surface.applyTo ?? [],
    });
  }

  applyMaterial(materialId: string, target: string): void {
    if (!this.model) return;

    applyMaterialToModel({
      model: this.model,
      materialId,
      target,
      findNodesByName: (name) => this.findNodesByName(name),
      onMaterialCloned: (material) => {
        if (material instanceof THREE.MeshStandardMaterial) {
          material.envMapIntensity = ENVIRONMENT.MAP_INTENSITY;
        }
        this.clonedMaterials.add(material);
      },
    });
  }

  dispose() {
    // Stop rendering
    this.isDisposed = true;
    this.onARSessionEnd = null;
    void this.exitAR();
    this.renderer.setAnimationLoop(null);
    this.isRendering = false;

    // Clean up observers and event listeners
    this.ro?.disconnect();
    this.ro = null;
    if (this.resizeHandler) {
      window.removeEventListener("resize", this.resizeHandler);
    }
    this.detachControls();

    // Dispose all materials from materialMap
    this.materialMap.forEach((material) => {
      material.dispose();
      if (material.map) {
        material.map.dispose();
      }
    });
    this.materialMap.clear();

    // Dispose all cached textures
    this.textureCache.forEach((texture) => {
      texture.dispose();
    });
    this.textureCache.clear();

    // Dispose all current textures
    this.currentTextures.forEach((texture) => {
      texture.dispose();
    });
    this.currentTextures.clear();

    this.clonedMaterials.forEach((material) => {
      material.dispose();
    });
    this.clonedMaterials.clear();

    // Dispose model and all its resources
    if (this.model) {
      this.scene.remove(this.model);
      this.model.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          const mesh = child as THREE.Mesh<
            THREE.BufferGeometry,
            THREE.Material | THREE.Material[]
          >;

          // Dispose geometry
          mesh.geometry.dispose();

          // Dispose materials and their textures
          if (Array.isArray(mesh.material)) {
            mesh.material.forEach((m) => {
              if (m instanceof THREE.Material) {
                m.dispose();
                if (m instanceof THREE.MeshStandardMaterial && m.map) {
                  m.map.dispose();
                }
              }
            });
          } else if (mesh.material instanceof THREE.Material) {
            mesh.material.dispose();
            if (
              mesh.material instanceof THREE.MeshStandardMaterial &&
              mesh.material.map
            ) {
              mesh.material.map.dispose();
            }
          }
        }
      });
      this.model = null;
    }

    // Dispose the IBL environment map.
    this.scene.environment = null;
    if (this.environmentMap) {
      this.environmentMap.dispose();
      this.environmentMap = null;
    }

    // Dispose the baked contact shadow (render targets, materials, geometry).
    if (this.shadowRebakeHandle !== null) {
      cancelAnimationFrame(this.shadowRebakeHandle);
      this.shadowRebakeHandle = null;
    }
    if (this.contactShadow) {
      this.scene.remove(this.contactShadow.group);
      this.contactShadow.dispose();
      this.contactShadow = null;
    }

    // Dispose renderer and remove DOM element
    this.renderer.dispose();
    if (this.mount.contains(this.renderer.domElement)) {
      this.mount.removeChild(this.renderer.domElement);
    }
  }
}
